import { type ComponentType, type Ref, type SVGProps, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Calendar,
  CircleQuestionMark,
  LayoutDashboard,
  LogOut,
  Menu as MenuIcon,
  MessageCircle,
  Route,
  Settings,
  Users,
  X,
} from 'lucide-react';
import {
  Avatar,
  IconButton,
  Logo,
  MobileHeader,
  MobileNav,
  MobileNavFooter,
  MobileNavHeader,
  MobileNavList,
  MobileNavSeparator,
  PracticeIcon,
  Rail,
  RailLabel,
  RailLogo,
  RailMenu,
  RailMenuContent,
  RailMenuItem,
  RailMenuSeparator,
  RailMenuTrigger,
  WundoChatIcon,
  mobileNavItemVariants,
  railLinkVariants,
} from '@wundo/ui';
import { nameToAbbreviate } from './nameToAbbreviate.ts';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { helpUrl } from '@wundo/shared/helpUrl.ts';
import { availableInDemo } from '@/lib/availableInDemo.ts';
import { canSeeDashboard } from '@/lib/canSeeDashboard.ts';
import { useDemoHoldBack } from '@/lib/useDemoHoldBack.ts';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import type { FileRouteTypes } from '@/routeTree.gen.ts';

interface NavItem {
  to: FileRouteTypes['to'];
  label: () => string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Absent means everyone. A gated item stays hidden until the user is known. */
  enabled?: (user: CurrentUser) => boolean;
}

/*
 * The one navigation definition. The rail and the mobile panel both render from
 * it, so they cannot drift apart in items, order, or which one is current —
 * two hand-maintained copies of this list is exactly how they diverged before.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: m.nav_agenda, icon: Calendar },
  { to: '/conversations', label: m.nav_conversations, icon: MessageCircle },
  { to: '/team', label: m.nav_team, icon: Users },
  {
    to: '/journeys',
    label: m.nav_journeys,
    icon: Route,
    enabled: (user) => user.journeys_enabled,
  },
  { to: '/practice', label: m.nav_practice, icon: PracticeIcon },
  {
    to: '/dashboard',
    label: m.nav_dashboard,
    icon: LayoutDashboard,
    enabled: canSeeDashboard,
  },
];

/* Filtered once for both the rail and the mobile panel, so a gate cannot hold in one and not the other. */
function navItemsFor(user: CurrentUser | undefined): readonly NavItem[] {
  return NAV_ITEMS.filter(
    (item) => !item.enabled || (user !== undefined && item.enabled(user)),
  );
}

/* A demo organisation is there to practise, so home is the practice library. */
function homeFor(user: CurrentUser | undefined): '/' | '/practice' {
  return user?.demo === true ? '/practice' : '/';
}

function useCurrentPath(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

/* Detail pages inherit their list page's section, so a prefix match is right for everything but "/". */
function isCurrent(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

export interface AppNavigationProps {
  /** Absent until the current user has been fetched — the rail renders anyway. */
  user: CurrentUser | undefined;
}

/**
 * You, at the foot of the rail: your picture, or your initials in your own tone.
 *
 * Fills the trigger rather than sizing itself, so the circle the popup anchors
 * to stays the rail's decision.
 *
 * A question mark while the user is still being fetched. The rail renders before
 * that answer arrives on purpose — blocking the whole frame on one disc is the
 * worse trade — and an empty circle reads as something broken.
 */
function AccountDisc({ user }: { user: CurrentUser | undefined }) {
  if (!user) return <Avatar className='size-full' name='?' seed='?' />;

  return (
    <Avatar
      className='size-full'
      src={user.profile_image_url}
      name={nameToAbbreviate(user)}
      seed={user.email ?? user.id}
    />
  );
}

export function AppRail({ user }: AppNavigationProps) {
  const pathname = useCurrentPath();
  const impersonating = !!useImpersonatedBy();
  const holdBack = useDemoHoldBack();

  return (
    <Rail
      aria-label={m.nav_main()}
      logo={
        <Link to={homeFor(user)} aria-label={m.nav_home()}>
          <RailLogo />
        </Link>
      }
      footer={
        <div className='flex w-full flex-col items-center gap-3'>
          <a
            href={helpUrl(getLocale())}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={m.nav_help()}
            className={railLinkVariants()}
          >
            <CircleQuestionMark className='size-5 shrink-0' aria-hidden='true' />
            <RailLabel>{m.nav_help()}</RailLabel>
          </a>
          <RailMenu>
            <RailMenuTrigger aria-label={m.nav_account()}>
              <AccountDisc user={user} />
            </RailMenuTrigger>
            <RailMenuContent>
              <RailMenuItem render={<Link to='/settings' />}>
                <Settings aria-hidden='true' />
                {m.nav_settings()}
              </RailMenuItem>
              <RailMenuSeparator />
              {/*
                A full navigation on purpose, in both branches: leaving a session
                should leave no cached client state behind. While impersonating,
                signing out would delete the impersonated session and strand the
                staff member's own — the only way out of that state is stopping.
              */}
              {impersonating ? (
                <RailMenuItem tone='danger' render={<a href='/stop-impersonating' />}>
                  <LogOut aria-hidden='true' />
                  {m.nav_stop_impersonating()}
                </RailMenuItem>
              ) : (
                <RailMenuItem tone='danger' render={<a href='/logout' />}>
                  <LogOut aria-hidden='true' />
                  {m.nav_sign_out()}
                </RailMenuItem>
              )}
            </RailMenuContent>
          </RailMenu>
        </div>
      }
    >
      {navItemsFor(user).map(({ to, label, icon: Icon }) => {
        const current = isCurrent(pathname, to);
        return (
          <Link
            key={to}
            to={to}
            /* Same words as the visible label, so the name matches what is on screen. */
            aria-label={label()}
            aria-current={current ? 'page' : undefined}
            className={railLinkVariants({ active: current })}
            {...holdBack(!availableInDemo(to))}
          >
            {/* DEV-36 settled the icon size at 20 — the 52px row math depends on it. */}
            <Icon className='size-5 shrink-0' aria-hidden='true' />
            <RailLabel>{label()}</RailLabel>
          </Link>
        );
      })}
    </Rail>
  );
}

export interface AppMobileNavProps extends AppNavigationProps {
  /** The phone's way into the chat: there is no strip to click at this width. Left out where there is no chat. */
  onOpenChat?: () => void;
  /** Where the caret goes when the drawer closes on a phone. */
  chatButtonRef?: Ref<HTMLButtonElement>;
}

export function AppMobileNav({ user, onOpenChat, chatButtonRef }: AppMobileNavProps) {
  const pathname = useCurrentPath();
  const [open, setOpen] = useState(false);
  const impersonating = !!useImpersonatedBy();
  const holdBack = useDemoHoldBack();

  return (
    <>
      <MobileHeader>
        <Link to={homeFor(user)} aria-label={m.nav_home()}>
          <Logo size='md' />
        </Link>
        <div className='flex items-center gap-2'>
          {onOpenChat && (
            <IconButton
              ref={chatButtonRef}
              aria-label={m.chat_open()}
              shape='square'
              onClick={onOpenChat}
            >
              <WundoChatIcon className='size-5' />
            </IconButton>
          )}
          <IconButton
            aria-label={m.nav_open_menu()}
            shape='square'
            onClick={() => setOpen(true)}
          >
            <MenuIcon className='size-5' aria-hidden='true' />
          </IconButton>
        </div>
      </MobileHeader>

      <MobileNav open={open}>
        <MobileNavHeader>
          <Logo size='md' />
          <IconButton
            aria-label={m.nav_close_menu()}
            shape='square'
            onClick={() => setOpen(false)}
          >
            <X className='size-5' aria-hidden='true' />
          </IconButton>
        </MobileNavHeader>

        {/* Mirrors the rail exactly. What lives in the account menu belongs in the footer, not here. */}
        <MobileNavList>
          {navItemsFor(user).map(({ to, label }) => {
            const current = isCurrent(pathname, to);
            const heldBack = holdBack(!availableInDemo(to));
            return (
              <Link
                key={to}
                to={to}
                aria-current={current ? 'page' : undefined}
                aria-disabled={heldBack['aria-disabled']}
                onClick={(event) => {
                  heldBack.onClick?.(event);
                  setOpen(false);
                }}
                className={mobileNavItemVariants({ active: current })}
              >
                {label()}
              </Link>
            );
          })}
        </MobileNavList>

        <MobileNavFooter>
          <MobileNavSeparator />
          <Link
            to='/settings'
            onClick={() => setOpen(false)}
            className={mobileNavItemVariants()}
          >
            {m.nav_settings()}
          </Link>
          <a
            href={helpUrl(getLocale())}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => setOpen(false)}
            className={mobileNavItemVariants()}
          >
            {m.nav_help()}
          </a>
          {impersonating ? (
            <a
              href='/stop-impersonating'
              className={mobileNavItemVariants({ tone: 'danger' })}
            >
              {m.nav_stop_impersonating()}
            </a>
          ) : (
            <a href='/logout' className={mobileNavItemVariants({ tone: 'danger' })}>
              {m.nav_sign_out()}
            </a>
          )}
        </MobileNavFooter>
      </MobileNav>
    </>
  );
}
